import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import projectService from '../../services/projectService';
import monitoringService from '../../services/monitoringService';
import inspectionService from '../../services/inspectionService';
import incidentService from '../../services/incidentService';
import InspectionDetailModal from '../../components/common/InspectionDetailModal';
import { CATEGORY_LABEL, CATEGORY_ICON, formatCategory } from '../inspections/categoryOptions';
import '../monitoring/Monitoring.css';
import './CategoryOverview.css';

// Incident.category is a fixed 6-value backend enum, distinct from the
// free-form, org-configurable category taxonomy everything else here uses.
// An Incidents section only makes sense to show when the current category
// slug happens to be one of these 6 values.
const INCIDENT_CATEGORIES = ['osh', 'environmental', 'community', 'labor', 'security', 'property'];

const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const CategoryOverview = () => {
  const { categorySlug } = useParams();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState('all');
  const [parameters, setParameters] = useState([]);
  const [entriesSummary, setEntriesSummary] = useState({ series: [], byParameter: [], totalEntries: 0 });
  const [inspections, setInspections] = useState([]);
  const [findings, setFindings] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingInspectionId, setViewingInspectionId] = useState(null);

  const showIncidents = INCIDENT_CATEGORIES.includes(categorySlug);
  const label = CATEGORY_LABEL[categorySlug] || formatCategory(categorySlug);
  const icon = CATEGORY_ICON[categorySlug] || 'fa-layer-group';

  const fetchProjects = useCallback(async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, []);

  const fetchCategoryData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { category: categorySlug };
      if (projectFilter !== 'all') params.projectId = projectFilter;

      const requests = [
        monitoringService.getParameters(params),
        monitoringService.getEntriesSummary(params),
        inspectionService.getAll(params),
        inspectionService.getFindings(params),
      ];
      if (showIncidents) requests.push(incidentService.getAll(params));

      const results = await Promise.all(requests);
      setParameters(results[0].parameters || []);
      setEntriesSummary(results[1] || { series: [], byParameter: [], totalEntries: 0 });
      setInspections(results[2].inspections || []);
      setFindings(results[3].findings || []);
      if (showIncidents) setIncidents(results[4].incidents || []);
      else setIncidents([]);
    } catch (error) {
      console.error('Failed to load category overview:', error);
      toast.error(error.response?.data?.error || 'Failed to load category data');
    } finally {
      setLoading(false);
    }
  }, [categorySlug, projectFilter, showIncidents]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { fetchCategoryData(); }, [fetchCategoryData]);

  const openFindings = useMemo(() => findings.filter((f) => f.status !== 'done'), [findings]);

  const newParameterHref = `/monitoring/parameters/new?category=${categorySlug}`;

  return (
    <div className="monitoring-page category-overview-page">
      <div className="monitoring-header">
        <div>
          <h1><i className={`fas ${icon}`}></i> {label}</h1>
          <p className="header-subtitle">
            Everything configured, inspected, and raised under {label} - across whichever projects you choose below.
          </p>
        </div>
        <div className="category-quick-actions">
          <Link to={newParameterHref} className="btn-secondary-outline">
            <i className="fas fa-sliders-h"></i> New Monitoring Parameter
          </Link>
          <button className="btn-primary-green" onClick={() => navigate('/inspections')}>
            <i className="fas fa-clipboard-check"></i> Start an Inspection
          </button>
          {showIncidents && (
            <button className="btn-secondary-outline" onClick={() => navigate('/incidents')}>
              <i className="fas fa-exclamation-triangle"></i> Report an Incident
            </button>
          )}
        </div>
      </div>

      <div className="monitoring-controls">
        <select className="filter-select" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
          <option value="all">All projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Loading {label} data...</h3>
        </div>
      ) : (
        <>
          <div className="stats-grid">
            <div className="stat-card total">
              <div className="stat-icon"><i className="fas fa-sliders-h"></i></div>
              <div><div className="stat-label">Configured Parameters</div><div className="stat-value">{parameters.length}</div></div>
            </div>
            <div className="stat-card success">
              <div className="stat-icon"><i className="fas fa-file-alt"></i></div>
              <div><div className="stat-label">Log Entries</div><div className="stat-value">{entriesSummary.totalEntries}</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon"><i className="fas fa-clipboard-check"></i></div>
              <div><div className="stat-label">Inspections</div><div className="stat-value">{inspections.length}</div></div>
            </div>
            <div className="stat-card danger">
              <div className="stat-icon"><i className="fas fa-exclamation-circle"></i></div>
              <div><div className="stat-label">Open Findings</div><div className="stat-value">{openFindings.length}</div></div>
            </div>
            {showIncidents && (
              <div className="stat-card warning">
                <div className="stat-icon"><i className="fas fa-exclamation-triangle"></i></div>
                <div><div className="stat-label">Incidents</div><div className="stat-value">{incidents.length}</div></div>
              </div>
            )}
          </div>

          {/* Configured Parameters */}
          <div className="category-section">
            <h3 className="category-section-title"><i className="fas fa-sliders-h"></i> Configured Parameters</h3>
            {parameters.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-sliders-h"></i>
                <h3>No monitoring parameters configured for {label} yet</h3>
                <p>Configure what you want to track for this category - it'll show up here and on Monitoring &gt; Configure.</p>
                <Link to={newParameterHref} className="btn-text">
                  <i className="fas fa-plus"></i> Configure a Parameter
                </Link>
              </div>
            ) : (
              <table className="parameter-table">
                <thead>
                  <tr>
                    <th>Parameter</th><th>Project</th><th>Frequency</th><th>Avg</th><th>Within Range</th><th>Out of Range</th>
                  </tr>
                </thead>
                <tbody>
                  {parameters.map((p) => {
                    const summary = entriesSummary.byParameter.find((b) => b.parameterId === p.id);
                    return (
                      <tr key={p.id}>
                        <td>{p.name}{p.unit ? ` (${p.unit})` : ''}</td>
                        <td>{p.project?.name || 'Org-wide'}</td>
                        <td>{p.frequency.replace('_', ' ')}</td>
                        <td>{summary?.average ?? '—'}</td>
                        <td>{summary?.withinRange ?? 0}</td>
                        <td>{summary?.outOfRange ?? 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Inspections */}
          <div className="category-section">
            <h3 className="category-section-title"><i className="fas fa-clipboard-check"></i> Inspections</h3>
            {inspections.length === 0 ? (
              <p className="category-empty-text">No {label} inspections submitted yet.</p>
            ) : (
              <div className="monitoring-list">
                {inspections.map((inspection) => (
                  <div key={inspection.id} className="monitoring-card" style={{ cursor: 'pointer' }} onClick={() => setViewingInspectionId(inspection.id)}>
                    <div className="monitoring-card-main">
                      <h4>{inspection.location || 'Site inspection'}</h4>
                      <div className="monitoring-card-meta">
                        <span><i className="fas fa-project-diagram"></i> {inspection.project?.name || 'Project'}</span>
                        <span><i className="fas fa-calendar"></i> {formatDate(inspection.submittedAt)}</span>
                        <span><i className="fas fa-percent"></i> {inspection.completionPercentage}% compliant</span>
                      </div>
                    </div>
                    <span className={`status-badge status-${inspection.overallCompliance}`}>{inspection.overallCompliance.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Compliance Findings */}
          <div className="category-section">
            <h3 className="category-section-title"><i className="fas fa-exclamation-circle"></i> Compliance Findings</h3>
            {findings.length === 0 ? (
              <p className="category-empty-text">No compliance findings raised for {label} yet.</p>
            ) : (
              <table className="parameter-table">
                <thead>
                  <tr>
                    <th>Title</th><th>Project</th><th>Severity</th><th>Status</th><th>RAG</th><th>Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  {findings.map((f) => (
                    <tr key={f.id}>
                      <td>{f.title}</td>
                      <td>{f.project?.name || '—'}</td>
                      <td>{f.severity}</td>
                      <td>{f.status.replace(/_/g, ' ')}</td>
                      <td><span className={`rag-badge rag-${f.rag.toLowerCase()}`}>{f.rag}</span></td>
                      <td>{f.deadline ? formatDate(f.deadline) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Incidents (only when the category maps to a real Incident category) */}
          {showIncidents && (
            <div className="category-section">
              <h3 className="category-section-title"><i className="fas fa-exclamation-triangle"></i> Incidents</h3>
              {incidents.length === 0 ? (
                <p className="category-empty-text">No {label} incidents reported yet.</p>
              ) : (
                <div className="monitoring-list">
                  {incidents.map((incident) => (
                    <div key={incident.id} className="monitoring-card">
                      <div className="monitoring-card-main">
                        <h4>{incident.title}</h4>
                        <div className="monitoring-card-meta">
                          <span><i className="fas fa-project-diagram"></i> {incident.project?.name || 'Project'}</span>
                          <span><i className="fas fa-calendar"></i> {formatDate(incident.occurredAt)}</span>
                        </div>
                      </div>
                      <span className={`status-badge status-${incident.status}`}>{incident.status.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <InspectionDetailModal inspectionId={viewingInspectionId} onClose={() => setViewingInspectionId(null)} />
    </div>
  );
};

export default CategoryOverview;
